import React, { RefObject } from "react";
import PathPickBasketListHeader from "@/features/pathpick/features/basket/components/list/PathPickBasketListHeader";
import PathPickBasketProductGrid from "@/features/pathpick/features/basket/components/list/PathPickBasketProductGrid";
import PathPickSectionCard from "@/features/pathpick/components/ui/PathPickSectionCard";
import PathPickPagination from "@/features/pathpick/components/ui/PathPickPagination";
import PathPickScrollToTopButton from "@/features/pathpick/components/ui/PathPickScrollToTopButton";
import useGetPathPickBasketProducts from "@/features/pathpick/features/basket/hooks/query/useGetPathPickBasketProducts";
import usePathPickBasketFilter from "@/features/pathpick/features/basket/hooks/usePathPickBasketFilter";
import usePathPickBasketSelection from "@/features/pathpick/features/basket/hooks/usePathPickBasketSelection";
import PathPickSelectionBarSpacer from "@/features/pathpick/components/ui/PathPickSelectionBarSpacer";

interface Props {
  // 페이지의 스크롤 Box — "맨 위로" 버튼이 이 요소를 직접 올린다.
  scrollContainerRef: RefObject<HTMLDivElement>;
}

// 담은 상품 리스트 섹션 — 헤더(필터)와 카드 리스트를 조합한다.
// KREAM 가격 기준은 조회 파라미터로도 나가므로 쿼리스트링(usePathPickBasketFilter)이 소유한다.
//
// 페이지네이션은 카드의 footer 슬롯에 둬야 sticky로 화면 바닥에 붙일 수 있어(본문에 두면
// 카드 본문의 하단 패딩 띠가 바 아래에 남는다) 그리드가 아니라 여기서 그린다.
// 목록 조회 훅은 캐시를 공유하므로 그리드와 여기서 함께 호출해도 요청이 늘지 않는다.
function PathPickBasketListSection({ scrollContainerRef }: Props) {
  const { items, totalCount, isPending, isPlaceholderData } =
    useGetPathPickBasketProducts();
  const { pageNumber, pageSize, setPageNumber, setPageSize } =
    usePathPickBasketFilter();
  // 선택이 있으면 플로팅 선택 바가 목록 하단을 덮는다.
  const { selectedCount } = usePathPickBasketSelection();

  // 빈 목록 안내에는 페이지네이션을 붙이지 않는다(전환 중에는 이전 총계로 자리를 지킨다)
  const hasPagination = isPlaceholderData || items.length > 0;
  // 스켈레톤이 아니라 실제 카드가 깔렸을 때만 "맨 위로"를 붙인다.
  const hasList = !isPending && !isPlaceholderData && items.length > 0;

  return (
    <PathPickSectionCard
      header={<PathPickBasketListHeader />}
      isFooterSticky
      footer={
        hasPagination ? (
          <PathPickPagination
            total={totalCount}
            pageNumber={pageNumber}
            pageSize={pageSize}
            // sticky footer가 기준 박스라 absolute면 닫힌 팝오버가 스크롤 영역을 늘린다.
            popoverStrategy={"fixed"}
            scrollContainerRef={scrollContainerRef}
            onChangePage={setPageNumber}
            onChangePageSize={setPageSize}
          />
        ) : undefined
      }
    >
      <PathPickBasketProductGrid />
      {/* 마지막 행 다음 줄 가운데 — 바닥까지 내렸을 때만 나타난다. */}
      {hasList && (
        <PathPickScrollToTopButton containerRef={scrollContainerRef} />
      )}
      {/* 플로팅 선택 바가 떠 있으면 끝까지 내려도 마지막 카드가 가리지 않게 비운다. */}
      {hasList && selectedCount > 0 && <PathPickSelectionBarSpacer />}
    </PathPickSectionCard>
  );
}

export default PathPickBasketListSection;
